import RestX from '~/RestX';
import i18n from '~/i18n';

// Utils
import asyncForEach from '~/utils/asyncForEach';
import hasDegreeChanged from '~/utils/degrees/hasDegreeChanged';
import hasLangChanged from '~/utils/languages/hasLangChanged';
import findDeletedEntities from '~/utils/findDeletedEntities';

class CandidateService {
    // SaveDegrees saves a bunch of degrees. It checks if new schools have changed,
    // checks for new degrees and updated degrees.
    static async saveDegrees(degrees, oldDegrees) {
        const errors = [];

        // Check every degree and check if school exists
        await asyncForEach(degrees, async degree => {
            // If school id does not exist
            if (!degree.school_id && degree.school) {
                const school = await RestX.getInterfaces()
                    .candidate.school.post({
                        body: {
                            name: degree.school.name,
                        },
                    })
                    .catch(err => errors.push(err));
                if (school) {
                    degree.school_id = school.id;
                }
            }

            // Don't want to send unecassary data
            delete degree.school; // Is necassary for avoiding school recreation/duplication
            delete degree.field;
            delete degree.level;

            // Check if degree has changed
            const hasChanged = hasDegreeChanged(degree, oldDegrees);

            if (degree.id && hasChanged) {
                // Identifer exists in degree => update degree
                await RestX.getInterfaces()
                    .candidate.degree.id(degree.id)
                    .put({
                        body: degree,
                    })
                    .catch(err => errors.push(err));
            } else if (!degree.id) {
                // No identifer in degree => create new degree
                await RestX.getInterfaces()
                    .candidate.degree.post({
                        body: degree,
                    })
                    .catch(err => errors.push(err));
            }
        });

        // Delete removed degrees
        const deletedDegrees = findDeletedEntities(degrees, oldDegrees);
        await asyncForEach(deletedDegrees, async degreeId => {
            await RestX.getInterfaces()
                .candidate.degree.id(degreeId)
                .delete({ body: {} });
        }).catch(err => errors.push(err));

        // Reject if errors, otherwise resolve
        if (errors.length > 0) {
            return Promise.reject(errors);
        }
        return Promise.resolve();
    }

    static async saveLanguages(languages, oldLanguages) {
        const errors = [];

        await asyncForEach(languages, async lang => {
            // Check if language has changed or not
            const hasChanged = hasLangChanged(lang, oldLanguages);
            const body = {
                languageId: lang.languageId,
                proficiencyId: lang.proficiencyId,
            }; // The payload to send
            if (lang.id && hasChanged) {
                // Identifer exists in language => update language
                await RestX.getInterfaces()
                    .candidate.language.id(lang.id)
                    .put({
                        body: body,
                    })
                    .catch(err => errors.push(err));
            } else if (!lang.id) {
                // No identifer in language => create new language
                await RestX.getInterfaces()
                    .candidate.language.post({
                        body: body,
                    })
                    .catch(err => errors.push(err));
            }
        }).catch(err => errors.push(err));

        // Check if some languages has been deleted
        const deletedLangs = findDeletedEntities(languages, oldLanguages);

        // Delete removed languages
        await asyncForEach(deletedLangs, async langId => {
            await RestX.getInterfaces()
                .candidate.language.id(langId)
                .delete({ body: {} })
                .catch(err => errors.push(err));
        }).catch(err => errors.push(err));

        // Reject if errors, otherwise resolve
        if (errors.length > 0) {
            return Promise.reject(errors);
        }
        return Promise.resolve();
    }

    static async saveUserData(userData) {
        return RestX.getInterfaces().user.me.put({
            body: userData,
        });
    }

    static async saveUserImage(image) {
        let formData = new FormData();
        formData.append('avatar', image);
        await RestX.getInterfaces()
            .user.avatar.post({
                body: formData,
            })
            .then(() => {})
            .catch(error => {
                console.log(error);
                RestX.getInterfaces().dialogModal.openModal({
                    title: i18n.t('spotlight.errors.imageUpload.title'),
                    content: i18n.t('spotlight.errors.imageUpload.message'),
                    isError: true,
                });
            });
    }
}

export default CandidateService;
